import { clients, HttpError } from 'lib/https';
import { useAuthStore } from 'state/store/auth/useAuthStore';
import { ConfigurationSettings } from '/models/configuration-settings';
import { trackEvent } from 'analytics/tracker';
import { EVENTS } from 'analytics/events';

export interface SignInResponse {
  access_token: string;
  refresh_token: string;
  enable_mfa: boolean;
  mfaId: string;
  mfaType: number;
}

interface AccountActivationData {
  password: string;
  code: string;
}

interface AccountActivationPayload extends AccountActivationData {
  ProjectKey: string;
  preventPostEvent: boolean;
}

export type PasswordSigninPayload = {
  grantType: 'password';
  username: string;
  password: string;
  captchaToken?: string;
  termsAndConditionsAccepted?: boolean;
};

export type SSoSigninPayload = {
  grantType: 'social';
  code: string;
  state: string;
};

export type MFASigninPayload = {
  grantType: 'mfa_code';
  code: string;
  mfaId: string;
  mfaType: number;
};

export type MFASigninResponse = {
  access_token: string;
  refresh_token: string;
};

export type ISignupPayload = {
  email: string;
  firstName: string;
  lastName: string;
  captchaCode: string;
  terms: boolean;
};

export interface ISignupResponse {
  itemId: string | null;
  errors: unknown | null;
  isSuccess: boolean;
}

export interface SigninBySSOPayload {
  grantType: 'social';
  code: string;
  state: string;
}

export const signin = async <T extends 'password' | 'social' | 'mfa_code' = 'password'>(
  payload: PasswordSigninPayload | MFASigninPayload | SigninBySSOPayload,
  config: ConfigurationSettings
): Promise<T extends 'password' ? SignInResponse : MFASigninResponse> => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.authentication.token}`;

  // sign in flow
  if (payload.grantType === 'password') {
    const passwordFormData = new URLSearchParams();
    if (payload.grantType === 'password') {
      passwordFormData.append('grant_type', 'password');
      passwordFormData.append('username', payload.username);
      passwordFormData.append('password', payload.password);
      if (payload.termsAndConditionsAccepted !== undefined) {
        passwordFormData.append('termsAndConditionsAccepted', payload.termsAndConditionsAccepted.toString());
      }
    }
    const response = await fetch(url, {
      method: 'POST',
      body: passwordFormData,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'x-blocks-key': config.blocksKey,
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const err = await response.json();
      trackEvent(EVENTS.ERROR(err, {
        endpoint: config.paths.authentication.token
      }))
      throw new HttpError(response.status, err);
    }

    return response.json();
  } else if (payload.grantType === 'social') {
    const signinBySSOData = new URLSearchParams();
    signinBySSOData.append('grant_type', 'social');
    signinBySSOData.append('code', payload.code);
    signinBySSOData.append('state', payload.state);

    const response = await fetch(url, {
      method: 'POST',
      body: signinBySSOData,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'x-blocks-key': config.blocksKey,
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const err = await response.json();
      trackEvent(EVENTS.ERROR(err, {
        endpoint: config.paths.authentication.token
      }))
      throw new HttpError(response.status, err);
    }

    return response.json();
  } else {
    // MFA OTP Verification flow
    const mfaFormData = new URLSearchParams();
    mfaFormData.append('grant_type', 'mfa_code');
    mfaFormData.append('code', payload.code || '');
    mfaFormData.append('mfa_id', payload.mfaId);
    mfaFormData.append('mfa_type', payload.mfaType.toString());

    const response = await fetch(url, {
      method: 'POST',
      body: mfaFormData,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'x-blocks-key': config.blocksKey,
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const err = await response.json();
      trackEvent(EVENTS.ERROR(err, {
        endpoint: config.paths.authentication.token
      }))
      throw new HttpError(response.status, err);
    }

    return response.json();
  }
};

export const signout = async (
  config: ConfigurationSettings
): Promise<{
  isSuccess: true;
}> => {
  try {
    localStorage.removeItem('auth-storage');
    const baseUrl = `https://${config.paths.baseUrl}`;
    const url = `${baseUrl}${config.paths.authentication.logout}`;
    return await clients.post(
      url,
      config.blocksKey,
      JSON.stringify({
        refreshToken: useAuthStore.getState().refreshToken,
      })
    );
  } catch (error) {
    console.error('Logout operation failed:', error);
    throw error;
  }
};

export const getRefreshToken = async (config: ConfigurationSettings) => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.authentication.token}`;
  const formData = new URLSearchParams();
  formData.append('grant_type', 'refresh_token');
  formData.append('refresh_token', useAuthStore.getState().refreshToken ?? '');

  const response = await fetch(url, {
    method: 'POST',
    body: formData,
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'x-blocks-key': config.blocksKey,
    },
    credentials: 'include',
  });

  const result = response.json();

  if (!response.ok) {
    trackEvent(EVENTS.ERROR(result, {
      endpoint: config.paths.authentication.token
    }))
  }

  return result;
};

export const accountActivation = async (
  data: {
    password: string;
    code: string;
    captchaCode: string;
  },
  config: ConfigurationSettings
) => {
  const payload: AccountActivationPayload = {
    ...data,
    ProjectKey: config.blocksKey,
    preventPostEvent: true,
  };

  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.iam.activateAccount}`;
  return clients.post(url, config.blocksKey, JSON.stringify(payload));
};

export const forgotPassword = async (
  data: {
    email: string;
    captchaCode?: string;
  },
  config: ConfigurationSettings
) => {
  const payload = {
    ...data,
    mailPurpose: 'RecoverAccount',
    ProjectKey: config.blocksKey,
  };

  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.iam.recoverAccount}`;
  return clients.post(url, config.blocksKey, JSON.stringify(payload));
};

export const resetPassword = async (
  data: { code: string; password: string },
  config: ConfigurationSettings
) => {
  const payload = {
    ...data,
    logoutFromAllDevices: true,
    ProjectKey: config.blocksKey,
  };

  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.iam.resetPassword}`;
  return clients.post(url, config.blocksKey, JSON.stringify(payload));
};

export const resendActivation = async (
  data: {
    userId: string;
  },
  config: ConfigurationSettings
) => {
  const payload = {
    ...data,
    mailPurpose: 'ResendActivation',
  };

  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.iam.resendActivation}`;
  return clients.post(url, config.blocksKey, JSON.stringify(payload));
};

export const logoutAll = async (config: ConfigurationSettings) => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.authentication.logoutAll}`;
  return clients.post(url, config.blocksKey, '');
};

export const signup = async (
  payload: ISignupPayload,
  config: ConfigurationSettings
): Promise<ISignupResponse> => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.identifier.signup}`;
  
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-blocks-key': config.blocksKey,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const err = await response.json();
    throw new HttpError(response.status, err);
  }

  return response.json();
};

/**
 * Generic function to refresh access token using refresh token
 * Can be called from anywhere in the application
 * @param config - Configuration settings containing API endpoints and blocks key
 * @returns Promise with new access token and refresh token
 * @throws HttpError if refresh token is invalid or expired
 */
export const refreshAccessToken = async (config: ConfigurationSettings) => {
  const authStore = useAuthStore.getState();
  
  if (!authStore.refreshToken) {
    throw new HttpError(401, { error: 'No refresh token available' });
  }

  try {
    const refreshTokenRes = await getRefreshToken(config);

    if (refreshTokenRes.error === 'invalid_refresh_token') {
      throw new HttpError(401, refreshTokenRes);
    }

    // Update the access token in the store
    authStore.setAccessToken(refreshTokenRes.access_token);

    return refreshTokenRes;
  } catch (error) {
    // If it's already an HttpError, re-throw it
    if (error instanceof HttpError) {
      throw error;
    }
    
    // For other errors, wrap them in an HttpError
    console.error('Failed to refresh access token:', error);
    throw new HttpError(500, { error: 'Failed to refresh access token' });
  }
};
